import type {
  ButtonAction,
  EditToolButtonAction,
  TourFrame,
  TourStep,
} from '@carboncopyco/types';
import { setActiveStepId } from '../../store';
import type { StepNavigationDirection } from '../../types/replayer';
import { getParentFrame } from '../../utils/replayer';
import { attachSession } from '../../utils/sessionForwarding';
import { useTourDispatch } from '../store';

interface UseIncrementalNavigation {
  playableFrames: TourFrame[];
  playableSteps: TourStep[];
  activeFrame: TourFrame;
  activeStep: TourStep;
  playFrame: (frame: TourFrame) => void;
  uuid: string;
}

export type UseIncrementalNavigationReturnType = (
  direction: StepNavigationDirection,
  keyboardNavigation?: boolean
) => void;

const getIncrementalDirectionOnClick = (direction: 'next' | 'back') => ({
  goToStepId: null,
  url: null,
  name: direction === 'next' ? 'nextStep' : 'previousStep',
});

export const useIncrementalNavigation = ({
  playableFrames,
  playableSteps,
  activeFrame,
  activeStep,
  playFrame,
  uuid,
}: UseIncrementalNavigation): UseIncrementalNavigationReturnType => {
  const dispatch = useTourDispatch();

  const performStateChange = (nextStepIndex: number) => {
    const nextStep = playableSteps[nextStepIndex];
    const parentFrame = getParentFrame(playableFrames, nextStep);

    if (parentFrame?.id !== activeFrame.id) {
      playFrame(parentFrame);
    }

    dispatch(setActiveStepId({ stepId: nextStep.stepId, uuid }));
  };

  const handleNextStep = (activeStepIndex: number) => {
    const stepIndexToPlay = activeStepIndex + 1;
    if (stepIndexToPlay === playableSteps.length) {
      return;
    }

    performStateChange(stepIndexToPlay);
  };

  const handlePreviousStep = (activeStepIndex: number) => {
    const stepIndexToPlay = activeStepIndex - 1;
    if (stepIndexToPlay === -1) {
      return;
    }

    performStateChange(stepIndexToPlay);
  };

  const handleStartOver = () => {
    performStateChange(0);
  };

  const handleCustomStep = (customStepId: TourStep['stepId']) => {
    if (!customStepId) {
      return;
    }

    const customStepIndex = playableSteps.findIndex(
      (playableStep) => playableStep.stepId === customStepId
    );
    if (customStepIndex === -1) {
      return;
    }

    performStateChange(customStepIndex);
  };

  const handleExternalUrl = (url: string) => {
    if (!url) {
      return;
    }

    const targetUrl = attachSession(url);
    window.open(targetUrl, '_blank');
  };

  const determineNavigationAction = (
    direction: StepNavigationDirection,
    activeStepIndex: number
  ) => {
    const {
      navigationOptions: {
        isButtonsNavigation,
        elementAction: elementNavigationAction,
      },
      buttons: {
        nextButton: { action: nextButtonAction },
        previousButton: { action: previousButtonAction },
        lastStepButton: { action: lastButtonAction },
      },
    } = activeStep.config.tooltip;

    /*
      - If right arrow is pressed we need to go next, respecting nextButtonAction
        - If it's the last step we need to respect the lastButtonAction and not nextButtonAction
      - If left arrow is pressed we need to go back, respecting previousButtonAction
    */
    const isLastStep = activeStepIndex === playableSteps.length - 1;
    const buttonNavigationAction =
      direction === 'next'
        ? !isLastStep
          ? nextButtonAction
          : lastButtonAction
        : previousButtonAction;

    /*
      - Since element navigation can only be a click if you are not using arrow navigation we
      decided to use only the right arrow as a trigger for element navigation actions
      - Left arrow is always mapped to go to the previous step
    */
    const elementNavigationActionMod =
      direction === 'next'
        ? elementNavigationAction
        : ({
            name: 'previousStep',
          } as EditToolButtonAction);

    /*
      - If button navigation is turned on and the tooltip is visible we always respect
      button navigation before element navigation since it makes sense:
        - Right Arrow = Next
        - Left Arrow = Back
    */
    const shouldRespectButtonNavigation =
      isButtonsNavigation && activeStep.showTooltip;
    return shouldRespectButtonNavigation
      ? buttonNavigationAction
      : elementNavigationActionMod;
  };

  const incrementalStepNavigation = (
    direction: StepNavigationDirection,
    keyboardNavigation = false
  ) => {
    const activeStepIndex = playableSteps.findIndex(
      (playableStep) => playableStep.stepId === activeStep.stepId
    );

    if (activeStepIndex === -1) {
      return;
    }

    const navigationAction = !keyboardNavigation
      ? getIncrementalDirectionOnClick(direction)
      : determineNavigationAction(direction, activeStepIndex);

    const navigationMap: {
      [key in ButtonAction]: () => void;
    } = {
      nextStep: () => handleNextStep(activeStepIndex),
      previousStep: () => handlePreviousStep(activeStepIndex),
      startOverTour: () => handleStartOver(),
      customStep: () => handleCustomStep(navigationAction.goToStepId),
      externalUrl: () => handleExternalUrl(navigationAction.url),
    };

    const navigationCallback = navigationMap[navigationAction.name];
    navigationCallback?.();
  };

  return incrementalStepNavigation;
};
